<template>
  <nx-dashboard-card
    :title="title"
    :loading="!currentSearch"
  >
    <template
      v-if="currentSearch"
      #content
    >
      <v-list dense>
        <nx-dashboard-menu-list-item
          v-for="search in formattedDefaultSearches"
          :key="search.uuid"
          :selected="search.uuid === currentSearch.uuid"
          :text="translate(search.name)"
          @click="setDefaultSearchAsCurrentSearch(search)"
        />

        <v-divider />

        <nx-saved-searches-menu-list
          :selected="currentSearch && currentSearch.uuid"
          @input="setSavedSearchAsCurrentSearch($event)"
        />

        <v-divider />

        <v-list-tile @click="openDialog('savedSearchesManager')">
          <v-list-tile-avatar>
            <v-icon>edit</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content v-text="$t('message.searcher.savedSearch.editSearches')" />
        </v-list-tile>

        <v-list-tile @click="openDialog('savedSearchesWizard')">
          <v-list-tile-avatar>
            <v-icon>search</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content v-text="$t('message.searcher.savedSearch.createNewSearch')" />
        </v-list-tile>
      </v-list>
    </template>

    <template v-slot:body>
      <v-layout class="nx-list row wrap">
        <nx-searcher-list
          v-if="filters"
          :entity="entity"
          :total-items="numResults"
          :items="formattedItems"
          :sort-filters="sortOptions"
          :sort="sort"
          :loading="loading"
          :view="view"
          :search-limit-options="searchLimitOptions"
          :search-limit="searchLimit"
          :search-results-have-changed="searchResultsHaveChanged"
          :search-results-diff="newResultsDiff"
          @input:view="view = $event"
          @input:sort-filters="updateCurrentSearchSort"
          @input:search-limit="updateCurrentSearchLimit"
          @click:refresh="refresh"
        />
      </v-layout>

      <nx-saved-searches-manage-dialog
        v-if="dialogState.savedSearchesManager.render"
        :value="dialogState.savedSearchesManager.active"
        @input:dialog="closeDialog('savedSearchesManager')"
      />

      <nx-saved-searches-wizard
        v-if="dialogState.savedSearchesWizard.render"
        :value="dialogState.savedSearchesWizard.active"
        :entity="entity"
        :entity-tabs="savedSearchesEntityTabs"
        @input="setNewSavedSearchAsCurrentSearch"
        @input:dialog="closeDialog('savedSearchesWizard')"
      />
    </template>

    <template v-slot:footer>
      <nx-dashboard-card-footer
        v-if="currentSearchRoute"
        :title="$t('message.common.viewMore')"
        :to="currentSearchRoute"
      />
    </template>
  </nx-dashboard-card>
</template>

<script>
import { cloneDeep, get } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { makeFormatter } from '@/modules/list/config';
import { LIST_VIEW_NORMAL } from '@/modules/searcher/config';
import { encodeSortString } from '@/modules/searcher/router';
import { apiToSearch } from '@/modules/searcher/api/saved-searches';
import NxSearcherList from '@/modules/searcher/SearchList';
import DashboardCard from '@/modules/dashboard/mixins/dashboard-card';
import NxDashboardCard from '@/modules/dashboard/components/DashboardCard';
import NxDashboardCardFooter from '@/modules/dashboard/components/DashboardCardFooter';
import NxDashboardMenuListItem from '@/modules/dashboard/components/DashboardMenuListItem';
import NxSavedSearchesMenuList from '@/modules/saved-searches/components/SavedSearchesMenuList.vue';
import NxSavedSearchesManageDialog from '@/modules/saved-searches/components/SavedSearchesManageDialog.vue';
import ManageDialogs, { initDialog } from '@/mixins/manage-dialogs';
import NxSavedSearchesWizard from '@/modules/saved-searches/components/SavedSearchesWizard.vue';
import { translateCandidates } from '@/utils';
import SearchableWithPolling from '@/modules/searcher/mixins/searchable-with-polling';

export default {
  name: 'NxDashboardPublicationsCard',
  components: {
    NxDashboardCard,
    NxDashboardCardFooter,
    NxDashboardMenuListItem,
    NxSavedSearchesWizard,
    NxSavedSearchesManageDialog,
    NxSavedSearchesMenuList,
    NxSearcherList,
  },
  mixins: [
    SearchableWithPolling,
    DashboardCard,
    ManageDialogs,
  ],
  props: {
    defaultSearches: {
      type: Array,
      required: true,
    },
    savedSearchesEntityTabs: {
      type: [Boolean, Array],
      default: false,
    },
  },
  data() {
    return {
      currentSearch: null,
      view: LIST_VIEW_NORMAL,
      dialogState: {
        savedSearchesManager: initDialog(),
        savedSearchesWizard: initDialog(),
      },
    };
  },
  computed: {
    ...mapState('searcher', ['savedSearches']),
    ...mapGetters('searcher', ['savedSearchByUuid']),
    ...mapGetters('dashboard', ['getCardStaffPreferences']),
    entity() {
      return this.currentSearch ? this.currentSearch.searcher : 'content';
    },
    formattedDefaultSearches() {
      return this.defaultSearches.map(apiToSearch);
    },
    title() {
      if (this.currentSearch) {
        return this.translate(this.currentSearch.name);
      }
      return this.$t('message.dashboard.publications.title');
    },
    formattedItems() {
      const formatter = makeFormatter(this.entity);
      // https://github.com/vuejs/vue/issues/2000#issuecomment-527146280
      return Object.freeze(
        this.items
          .map(item => item.entity)
          .map(item => ({
            siteId: item.siteId,
            ...(item.list ? item.list : formatter(item)),
          })),
      );
    },
    sortOptions() {
      return get(this.$store.getters.searcher, `${this.entity}.sort.available`, []);
    },
    filters() {
      if (!this.currentSearch) return null;
      return this.currentSearch.filters;
    },
    sort() {
      if (!this.currentSearch) return null;
      return this.currentSearch.sort;
    },
    currentSearchRoute() {
      if (!this.filters) return null;
      const query = cloneDeep(this.filters);
      if (this.sort) {
        query.sort = encodeSortString(this.sort);
      }
      return {
        name: 'searcher',
        params: { entity: 'content' },
        query,
      };
    },
  },
  watch: {
    defaultSearches: {
      handler() {
        this.init();
      },
      immediate: true,
    },
  },
  methods: {
    init() {
      const { searchUuid } = this.staffPreferences || { searchUuid: null };
      const initSearch = this.defaultSearchByUuid(searchUuid)
        || this.savedSearchByUuid(searchUuid)
        || this.getFirstAvailableSavedSearch();
      this.currentSearch = cloneDeep(initSearch);
    },
    defaultSearchByUuid(uuid) {
      return this.formattedDefaultSearches.find(search => search.uuid === uuid);
    },
    getFirstAvailableSavedSearch() {
      if (this.formattedDefaultSearches.length) {
        return this.formattedDefaultSearches[0];
      }
      if (this.savedSearches.length) {
        return apiToSearch(this.savedSearches[0]);
      }
      return null;
    },
    setSavedSearchAsCurrentSearch(savedSearch) {
      this.currentSearch = cloneDeep(savedSearch);
      this.saveCurrentSearch();
    },
    setDefaultSearchAsCurrentSearch(defaultSearch) {
      // Default searches are not linked to Vuex because they are copied in `formattedDefaultSearches` into simple
      // objects. Because of that, there is no need to clone the objects.
      this.currentSearch = defaultSearch;
      this.saveCurrentSearch();
    },
    setNewSavedSearchAsCurrentSearch(newSearch) {
      this.setDefaultSearchAsCurrentSearch(newSearch);
    },
    saveCurrentSearch() {
      this.$store.dispatch('dashboard/SAVE_PUBLICATIONS_SEARCH', {
        cardId: this.id,
        searchUuid: this.currentSearch.uuid,
      });
    },
    updateCurrentSearchSort(sort) {
      this.currentSearch.sort = sort;
    },
    updateCurrentSearchLimit(limit) {
      this.resetPage();
      this.currentSearch.filters.limit = limit;
    },
    translate(translatable, translationVars = []) {
      return translateCandidates(
        [
          `message.${translatable}`,
          translatable,
        ],
        translatable,
        translationVars,
      );
    },
  },
};
</script>
